// web/src/firebase.ts
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import {
  getAuth, GoogleAuthProvider, signInWithRedirect, getRedirectResult,
  onAuthStateChanged, signOut, type User,
} from "firebase/auth";
import { nameFromEmail } from "./name";
import { resolveAuthDomain } from "./authDomain";
import { getNameOverride, clearNameOverride } from "./storage";

// How long we wait for Firebase Auth to report an initial state before giving
// up and rendering the sign-in screen anyway. Without this, a stuck SDK
// (persistence unavailable, deleted app instance, …) leaves the app on the
// boot spinner forever with no way out.
const AUTH_STATE_TIMEOUT_MS = 8000;

// Surfaced to the UI (see SignIn.tsx) the next time it asks. Cleared on read.
// `getRedirectResult` is the only place OAuth/config errors (unauthorized
// domain, network failure, account conflicts, …) become observable, so a
// silent catch there means a broken sign-in shows nothing but an endless
// bounce back to the sign-in screen.
let lastAuthError: string | null = null;

export function getLastAuthError(): string | null {
  const err = lastAuthError;
  lastAuthError = null;
  return err;
}

function describeAuthError(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === "auth/unauthorized-domain") {
    return "Diese Domain ist für die Anmeldung nicht freigegeben.";
  }
  if (code === "auth/network-request-failed") {
    return "Netzwerkfehler bei der Anmeldung. Bitte erneut versuchen.";
  }
  return "Anmeldung fehlgeschlagen. Bitte erneut versuchen.";
}

// In dev, surface a debug token in the browser console so the emulator (or App Check
// debug allowlist) accepts our calls. This MUST run before initializeAppCheck.
if (import.meta.env.DEV) {
  // @ts-expect-error — Firebase recognizes this global at runtime.
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Resolved from the host actually serving the page, not baked in: the app is
  // reachable on two Hosting domains and the redirect flow needs the auth
  // handler to be same-origin on whichever one the user opened.
  authDomain: resolveAuthDomain(
    typeof window === "undefined" ? "" : window.location.hostname,
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  ),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);

let appCheck: AppCheck | null = null;
const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (siteKey) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export function getAppCheckInstance(): AppCheck | null { return appCheck; }

const auth = getAuth(app);

export interface SessionUser {
  uid: string;
  email: string;
  displayName: string | null;
}

function toSession(user: User | null): SessionUser | null {
  if (!user || !user.email) return null;
  return { uid: user.uid, email: user.email, displayName: user.displayName };
}

/**
 * Resolve the current session, completing a redirect sign-in if we just came
 * back from Google. Returns null when nobody is signed in.
 */
export async function currentSession(): Promise<SessionUser | null> {
  try {
    const result = await getRedirectResult(auth);
    if (result?.user) {
      // A transient redirect error earlier in this same boot (or a leftover
      // from a previous one) is meaningless once we actually have a valid
      // session — never let it resurface after a later, successful sign-out.
      lastAuthError = null;
      return toSession(result.user);
    }
  } catch (err) {
    // A genuine redirect failure (unauthorized domain, network error, account
    // conflict, …) must not vanish silently — log it and stash a message the
    // sign-in screen can show, then still fall through to the observer below:
    // the user may already have a valid session independent of this redirect.
    console.error("[auth] getRedirectResult failed", err);
    lastAuthError = describeAuthError(err);
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (session: SessionUser | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      if (session) lastAuthError = null;
      resolve(session);
    };
    const timer = setTimeout(() => {
      console.error("[auth] onAuthStateChanged did not fire within timeout");
      finish(null);
    }, AUTH_STATE_TIMEOUT_MS);
    const unsub = onAuthStateChanged(
      auth,
      (user) => finish(toSession(user)),
      (err) => {
        console.error("[auth] onAuthStateChanged error", err);
        lastAuthError = describeAuthError(err);
        finish(null);
      },
    );
  });
}

export function startSignIn(): Promise<void> {
  const provider = new GoogleAuthProvider();
  // `hd` only pre-filters the account chooser; the server enforces the domain.
  provider.setCustomParameters({ hd: "bitforge.ch", prompt: "select_account" });
  return signInWithRedirect(auth, provider);
}

export async function signOutNow(): Promise<void> {
  // Always drop the local name override on sign-out — it is per-account, and
  // a stale override would leak the previous user's typed name onto the next
  // person who signs in on this device.
  try {
    await signOut(auth);
  } finally {
    clearNameOverride();
  }
}

export async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error("not signed in");
  return user.getIdToken();
}

/** Override (scoped to this account), else the Google profile name, else derived from the address. */
export function resolveName(session: SessionUser): string {
  const override = getNameOverride(session.email);
  if (override) return override;
  if (session.displayName && session.displayName.trim()) return session.displayName.trim();
  return nameFromEmail(session.email);
}
